import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import Sidebar from './Sidebar'
import KpiCard from './KpiCard'
import AdminChat from './AdminConsole/AdminChat'
import AgentsPage from './AgentsPage'
import ControlsPage from './ControlsPage'
import NotificationsPage from './NotificationsPage'
import ApprovalsPage from './ApprovalsPage'
import BuildAgent from './AdminConsole/BuildAgent'
import EvaluatePanel from './AdminConsole/EvaluatePanel'
import { Icon } from './Brand'
import { tintMap } from '../data'
import { relTime } from '../lib/time'
import { isAdmin } from '../lib/user'
import { loadSession, saveSession, appendToUserSession, emptySession } from '../lib/sessionStore'
import { getApprovals, addApproval, removeApproval } from '../lib/approvalsStore'
import { getEvalPassRate } from '../lib/evalStore'
import CollectionsWorkspace from '../collections/CollectionsWorkspace'
import { buildAgentRoster, fetchCollectionsAgentRegistry } from '../collections/agents'

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// A real backend sub-agent (from GET /api/config) as a card.
function RealAgentCard({ agent, lastUsed, index }) {
  const used = lastUsed && lastUsed.toLowerCase() === agent.name.toLowerCase()
  return (
    <motion.div
      className="agent"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.1 + index * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="agent-top">
        <div className="agent-ic" style={{ background: 'var(--lav-50)' }}>
          <Icon name="smart_toy" style={{ color: 'var(--lav-400)' }} />
        </div>
        <div>
          <div className="agent-name">{cap(agent.name)}</div>
          <div className="agent-role mono">{agent.tool_name}</div>
        </div>
        <span className={`status ${used ? 'live' : 'idle'}`}>
          <span className="dot" />
          {used ? 'Last used' : 'Ready'}
        </span>
      </div>
      <div className="agent-job">
        <div className="jl">Capabilities granted</div>
        <div className="ctl-caps" style={{ marginTop: 6, marginLeft: 0 }}>
          {(agent.capabilities || []).length === 0 ? (
            <span className="ctl-cap muted">{agent.custom ? 'prompt-only' : 'no access'}</span>
          ) : (
            agent.capabilities.map((c) => <span className="ctl-cap" key={c}>{c}</span>)
          )}
        </div>
      </div>
    </motion.div>
  )
}

function BackendRow({ label, value }) {
  return (
    <div className="be-row">
      <span className="be-lab">{label}</span>
      <span className="be-val">{value}</span>
    </div>
  )
}

// The fully backend-backed dashboard: real agents + config from /api/config,
// real KPIs from this session's Chat activity, real-only activity feed.
function RealDashboard({ config, metrics, live, user }) {
  const provider = config?.model_provider
  const agents = config?.agents || []
  const lastAgent = live[0]?.agent
  const firstName = (user?.name || 'there').split(' ')[0]
  // The per-chat judge verdict is gone; surface the last Evaluate pass-rate
  // instead (null → "—" until an evaluation has run).
  const evalRate = getEvalPassRate()

  const kpis = [
    { id: 'a', icon: 'smart_toy', tint: 'peach', value: agents.length, decimals: 0, label: 'Agents active', meta: provider ? `on ${provider}` : 'backend' },
    { id: 'q', icon: 'forum', tint: 'coral', value: metrics.questions, decimals: 0, label: 'Questions handled', meta: 'this session' },
    { id: 'v', icon: 'verified_user', tint: 'teal', value: evalRate == null ? null : Math.round(evalRate * 100), decimals: 0, unit: '%', label: 'Eval pass rate', meta: 'last run' },
    { id: 's', icon: 'database', tint: 'lav', value: metrics.sources, decimals: 0, label: 'Sources cited', meta: 'across answers' },
  ]

  return (
    <div className="main-inner">
      <motion.div
        className="page-head"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <div>
          <div className="eyebrow">Live · {provider || 'backend'}</div>
          <h1>Good morning, {firstName}</h1>
          <p className="sub">Real activity from your agentframe backend, this session.</p>
        </div>
        <div className="period">
          <Icon name="bolt" />
          {provider || '—'}
        </div>
      </motion.div>

      <div className="section-label">This session</div>
      <div className="kpi-grid">
        {kpis.map((k, i) => <KpiCard key={k.id} kpi={k} index={i} />)}
      </div>

      <div className="section-label">
        Sub-agents
        <span className="live">
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--teal-500)' }} />
          {agents.length} online
        </span>
      </div>
      <div className="agent-grid">
        {agents.length === 0 ? (
          <div className="ph-sub">Loading agents from the backend (GET /api/config)…</div>
        ) : (
          agents.map((a, i) => <RealAgentCard key={a.name} agent={a} lastUsed={lastAgent} index={i} />)
        )}
      </div>

      <div className="section-label">Live activity</div>
      <motion.div
        className="bottom-grid"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="panel">
          <h3>Activity feed</h3>
          <div className="ph-sub">Real requests answered by your agents</div>
          <div className="feed">
            {live.length === 0 && (
              <div className="ph-sub" style={{ marginBottom: 0 }}>
                No activity yet — ask a question in the Chat tab.
              </div>
            )}
            {live.map((r) => {
              const tint = tintMap[r.tint] || tintMap.lav
              return (
                <div className="feed-row" key={r.id}>
                  <div className="feed-ic" style={{ background: tint.bg }}>
                    <Icon name={r.icon} style={{ color: tint.fg }} />
                  </div>
                  <div className="ft">
                    <b>{r.agent}</b> <span>{r.text}</span>
                  </div>
                  <div className="fm">{relTime(r.when)}</div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="panel">
          <h3>Backend</h3>
          <div className="ph-sub">Live configuration</div>
          <div className="be-list">
            <BackendRow label="Model provider" value={provider || '—'} />
            <BackendRow label="Fail-closed judge" value={config?.judge_enabled ? 'On' : 'Off'} />
            <BackendRow label="Constraint lookup" value={config?.eval_lookup_enabled ? 'On' : 'Off'} />
            <BackendRow label="Sub-agents" value={String(agents.length)} />
          </div>
        </div>
      </motion.div>
    </div>
  )
}

function ComingSoon({ view }) {
  const copy = { chat: { icon: 'forum', title: 'Chat' } }[view] || { icon: 'dashboard', title: 'Coming soon', line: '' }
  return (
    <div className="main-inner">
      <motion.div
        className="coming-soon"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="cs-ic"><Icon name={copy.icon} /></div>
        <h2>{copy.title}</h2>
        <p>{copy.line}</p>
      </motion.div>
    </div>
  )
}

export default function Dashboard({ view = 'dashboard', onNavigate = () => {}, routeTarget = {}, user = null, onLogout = () => {} }) {
  // Seed this user's session from the per-user store so a returning user gets
  // their history back after logout→login (cleared on full page reload).
  const [init] = useState(() => loadSession(user?.user_id))
  const [liveActivity, setLiveActivity] = useState(init.liveActivity)
  const [metrics, setMetrics] = useState(init.metrics)
  const [config, setConfig] = useState(null)
  const [collectionsAgents, setCollectionsAgents] = useState({
    data: null,
    availability: 'loading',
    reason: null,
  })
  const [notifs, setNotifs] = useState(init.notifs)
  // Approvals are a SHARED cross-user queue (not per-user): a request one user
  // raises is actioned by whichever admin signs in. Mirror the global store here.
  const [apprs, setApprs] = useState(() => getApprovals())
  // Chat conversation lives HERE (the persistent shell), not inside <Chat>, so
  // the history + session survive switching tabs (Chat unmounts on nav away).
  const [chatMessages, setChatMessages] = useState(init.chatMessages)
  const chatSession = useRef(
    init.sessionId ||
    ((typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : 'demo-session')
  )
  // The admin Chat tab tests one agent at a time via per-agent threads. This
  // in-memory session lives on the persistent shell so each agent's history
  // survives switching tabs (kept separate from the orchestrator chatMessages).
  const adminChatSession = useRef(emptySession())

  // Persist this user's session on every change, so it survives logout→login.
  useEffect(() => {
    saveSession(user?.user_id, {
      chatMessages, notifs, liveActivity, metrics, sessionId: chatSession.current,
    })
  }, [user, chatMessages, notifs, liveActivity, metrics])

  // Pull the live backend config once (provider + real sub-agents).
  const refetchConfig = useCallback(() => {
    fetch('/api/config')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setConfig(d) })
      .catch(() => {})
  }, [])

  useEffect(() => { refetchConfig() }, [refetchConfig])

  const refetchCollectionsAgents = useCallback(() => {
    setCollectionsAgents((current) => ({
      ...current,
      availability: current.data ? 'refreshing' : 'loading',
    }))
    return fetchCollectionsAgentRegistry().then(setCollectionsAgents).catch(() => {})
  }, [])

  useEffect(() => { refetchCollectionsAgents() }, [refetchCollectionsAgents])

  const refreshAgentRosters = useCallback(() => {
    refetchConfig()
    return refetchCollectionsAgents()
  }, [refetchCollectionsAgents, refetchConfig])

  useEffect(() => {
    if (view === 'agents') refreshAgentRosters()
  }, [refreshAgentRosters, view])

  const pushActivity = (event) => {
    setLiveActivity((prev) => [event, ...prev].slice(0, 8))
    setMetrics((m) => ({
      questions: m.questions + 1,
      tables: m.tables + (event.hasTable ? 1 : 0),
      sources: m.sources + (event.sourceCount || 0),
      verified: (m.verified || 0) + (event.verified ? 1 : 0),
    }))
    setNotifs((prev) => [{
      id: event.id,
      icon: 'forum',
      tint: event.tint || 'lav',
      title: `${event.agent} answered a question`,
      text: event.summary || 'answered',
      when: event.when,
      read: false,
    }, ...prev])
  }

  const pushFlagged = (event) => {
    const short = event.prompt.length > 80 ? event.prompt.slice(0, 80) + '…' : event.prompt
    setNotifs((prev) => [{
      id: event.id,
      icon: 'gpp_bad',
      tint: 'coral',
      title: 'Security policy blocked a request',
      text: short,
      when: event.when,
      read: false,
    }, ...prev])
    // Raise into the shared queue, tagged with who raised it, so an admin can
    // act on it later — even after this user signs out.
    addApproval({
      id: event.id,
      icon: 'gpp_bad',
      tint: 'coral',
      title: 'Request blocked by security policy',
      detail: event.prompt,
      agent: 'Security policy',
      when: event.when,
      requester: { user_id: user?.user_id, name: user?.name || 'Unknown' },
    })
    setApprs(getApprovals())
  }
  const markNotifRead = (id) =>
    setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
  const markAllNotifsRead = () => setNotifs((prev) => prev.map((n) => ({ ...n, read: true })))
  // Route an executed answer / notification to the ORIGINAL requester: if that's
  // the current user, update live state; otherwise write into their stored
  // session so it's waiting for them when they next sign in.
  const deliverToRequester = (requester, message, notif) => {
    if (requester?.user_id && requester.user_id === user?.user_id) {
      if (message) setChatMessages((m) => [...m, message])
      if (notif) setNotifs((prev) => [notif, ...prev])
    } else {
      appendToUserSession(requester?.user_id, { message, notif })
    }
  }

  // Resolve a shared-queue approval. 'denied' upholds the block; 'approved'
  // re-runs the blocked prompt with an admin override and delivers the answer to
  // the ORIGINAL requester's chat (so they see it when they next sign in).
  const resolveApproval = async (id, decision = 'approved') => {
    if (!isAdmin(user)) return
    const card = getApprovals().find((a) => a.id === id)
    removeApproval(id)
    setApprs(getApprovals())
    if (!card) return
    const requester = card.requester || { user_id: user?.user_id, name: user?.name }
    const short = card.detail.length > 48 ? card.detail.slice(0, 48) + '…' : card.detail

    if (decision !== 'approved') {
      deliverToRequester(requester, null, {
        id: `deny-${id}`, icon: 'gpp_bad', tint: 'coral',
        title: 'Security block upheld', text: `"${short}" was not allowed`,
        when: Date.now(), read: false,
      })
      return
    }

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          prompt: card.detail, sessionId: chatSession.current,
          userId: user?.user_id, override: true,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.error) {
        // Put the card back — the block was neither upheld nor overridden, so
        // the decision must stay pending instead of vanishing from the queue.
        addApproval(card)
        setApprs(getApprovals())
        const text = typeof data.error === 'string' ? data.error : `Request failed (${res.status}).`
        setNotifs((prev) => [{
          id: `ovr-${id}`, icon: 'error', tint: 'coral',
          title: 'Override failed — request returned to the queue', text, when: Date.now(), read: false,
        }, ...prev])
        return
      }
      const message = {
        role: 'assistant',
        text: data.message || '',
        artifacts: data.artifacts || [],
        sources: data.sources || [],
      }
      // The answer lands in the requester's chat + notifies them.
      deliverToRequester(requester, message, {
        id: `ovr-${id}`, icon: 'lock_open', tint: 'teal',
        title: 'Override approved — request executed',
        text: `Your blocked request ran: "${short}"`, when: Date.now(), read: false,
      })
      // Feedback to the admin who approved (unless they were the requester).
      if (requester.user_id !== user?.user_id) {
        setNotifs((prev) => [{
          id: `ovr-admin-${id}`, icon: 'lock_open', tint: 'teal',
          title: 'Override executed', text: `Ran ${requester.name}'s blocked request`,
          when: Date.now(), read: false,
        }, ...prev])
      }
    } catch {
      // Same as the error branch: a network failure must not drop the pending
      // decision from the shared queue.
      addApproval(card)
      setApprs(getApprovals())
      setNotifs((prev) => [{
        id: `ovr-${id}`, icon: 'error', tint: 'coral',
        title: 'Override failed — request returned to the queue',
        text: 'Could not reach the backend to execute the override.',
        when: Date.now(), read: false,
      }, ...prev])
    }
  }

  const notifCount = notifs.filter((n) => !n.read).length
  const approvalCount = apprs.length
  const agentCount = buildAgentRoster(config?.agents || [], collectionsAgents.data).visibleCount

  return (
    <div className="app">
      <Sidebar
        activeView={view}
        onNavigate={onNavigate}
        notifCount={notifCount}
        approvalCount={approvalCount}
        agentCount={agentCount}
        user={user}
        onLogout={onLogout}
      />
      <main className="main">
        {view.startsWith('collections-') ? (
          <CollectionsWorkspace view={view} onNavigate={onNavigate} target={routeTarget} user={user} />
        ) : view === 'chat' ? (
          <AdminChat
            agents={config?.agents || []}
            user={user}
            session={adminChatSession.current}
          />
        ) : view === 'dashboard' ? (
          <RealDashboard config={config} metrics={metrics} live={liveActivity} user={user} />
        ) : view === 'agents' ? (
          <div className="main-inner">
            <AgentsPage
              agents={config?.agents || []}
              collectionsRegistry={collectionsAgents}
              live={liveActivity}
              onChanged={refetchConfig}
              onNavigate={onNavigate}
              onRefresh={refreshAgentRosters}
            />
          </div>
        ) : view === 'build' ? (
          <div className="main-inner"><BuildAgent onCreated={refetchConfig} /></div>
        ) : view === 'evaluate' ? (
          <div className="main-inner"><EvaluatePanel /></div>
        ) : view === 'controls' ? (
          <div className="main-inner"><ControlsPage /></div>
        ) : view === 'notifications' ? (
          <div className="main-inner">
            <NotificationsPage items={notifs} onRead={markNotifRead} onReadAll={markAllNotifsRead} />
          </div>
        ) : view === 'approvals' ? (
          <div className="main-inner">
            <ApprovalsPage items={apprs} onResolve={resolveApproval} role={user?.role} />
          </div>
        ) : (
          <ComingSoon view={view} />
        )}
      </main>
    </div>
  )
}
